import React, { useState } from 'react';
import { Check, X, ArrowRight, Layers, Clock, BarChart3, Users, Shield, Briefcase, RefreshCw } from 'lucide-react';
import { motion } from 'framer-motion';
import { CampaignSelector } from '../components/CampaignSelector';
const fmt = (n: number) => n.toLocaleString('en-US');
export function SummitProduct() {
  const [campaignMode, setCampaignMode] = useState(1);
  const isPartial = campaignMode === 0;
  const campaigns = isPartial ? 1 : campaignMode;
  const buildFee = isPartial ? 6500 : 6500 + 1000 * (campaignMode - 1);
  const systemFee = 2500;
  const campaignFee = isPartial ? 1250 : 2500 * campaigns;
  const corpRecruiter = isPartial ? 1500 : 3000 * campaigns - 500 * (campaigns - 1);
  const monthlyTotal = systemFee + campaignFee + corpRecruiter;
  return <div className="min-h-screen w-full bg-[#0A0F1C] text-slate-200 selection:bg-amber-500/30 font-sans" data-id="element-1111" data-mp="2yg2p1an8rik2">
      {/* 1. HERO */}
      <section className="relative pt-32 pb-8 px-6 md:px-12 max-w-7xl mx-auto text-center" data-id="element-1112" data-mp="2uer59lr1trjs">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-96 bg-amber-500/5 blur-[120px] rounded-full pointer-events-none" data-id="element-1113" data-mp="igbdiebrryaz" />

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} data-id="element-1114" data-mp="8ix6t6fnenvv">
          <div className="inline-block px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400 text-xs font-bold uppercase tracking-widest mb-6" data-id="element-1115" data-mp="118w0jf4sf96v">
            Embedded Execution
          </div>
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 tracking-tight" data-id="element-1116" data-mp="s6d2r9fuzmrh">
            HIRE Summit
          </h1>
          <p className="text-xl md:text-2xl text-slate-400 max-w-3xl mx-auto leading-relaxed" data-id="element-1117" data-mp="wac9x1f91986">
            Summit is designed for teams who want recruiting execution handled
            so leadership can stay focused on production and growth.
          </p>
        </motion.div>
      </section>

      {/* 2. PRICING */}
      <section className="pt-4 pb-20 px-6 md:px-12 max-w-6xl mx-auto" data-id="element-1118" data-mp="1ih6qp0rnxtay">
        <CampaignSelector mode={campaignMode} onChange={setCampaignMode} accent="amber" data-id="element-1119" data-mp="1ij9cf8ls909v" />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1120" data-mp="18rn53nckflce">
          {/* Build Fee */}
          <div className="bg-slate-900/40 border border-slate-800 p-6 rounded-xl relative overflow-hidden" data-id="element-1121" data-mp="14p8nt7ojotn8">
            <div className="mb-4" data-id="element-1122" data-mp="zpvz0he45ci0">
              <div className="text-xs text-slate-500 uppercase tracking-widest mb-2" data-id="element-1123" data-mp="zoucngrnd3kz">
                Build and Implementation (One-Time)
              </div>
              <div className="text-3xl font-bold text-white" data-id="element-1124" data-mp="psrboe8j7azm">
                ${fmt(buildFee)}
              </div>
              <ul className="mt-5 space-y-2.5 pt-4 border-t border-slate-800/50" data-id="element-1125" data-mp="2p5lp6uv3tv1g">
                {['Company and opportunity profiling', 'Custom ATS with dashboards, workflows, and job board integrations', 'Culture video and ad copywriting', 'Landing pages and candidate application flows', 'Full funnel design, from ad to onboarding', 'Compensation and role benchmarking by region', 'Pre-launch system stress test', 'Team training and 90-day coaching to optimize output'].map((point, i) => <li key={i} className="flex items-start" data-id="element-1126" data-mp="29qrqgbd0qxfb" data-mpi={i}>
                    <Check className="w-3.5 h-3.5 text-amber-400 mr-2 mt-0.5 shrink-0" data-id="element-1127" data-mp="2dqs61zbuteny" />
                    <span className="text-slate-400 text-sm leading-snug" data-id="element-1128" data-mp="2io2423db6yne">
                      {point}
                    </span>
                  </li>)}
              </ul>
            </div>
          </div>

          {/* Monthly Total */}
          <div className="bg-slate-900/40 border border-amber-500/30 p-6 rounded-xl relative" data-id="element-1129" data-mp="2xyyxpt86ayr">
            <div className="absolute top-0 left-0 w-full h-1 bg-amber-500 rounded-t-xl" data-id="element-1130" data-mp="1buw3zd0qccug" />
            <div className="mb-4" data-id="element-1131" data-mp="1bwqq2unqmb04">
              <div className="text-xs text-amber-400 uppercase tracking-widest mb-2" data-id="element-1132" data-mp="16wckdj9lzd59">
                Total Monthly
              </div>
              <div className="text-4xl font-bold text-white mb-1" data-id="element-1133" data-mp="1kx5dbzk9i069">
                ${fmt(monthlyTotal)}
              </div>
              <div className="text-sm text-slate-500 mb-6" data-id="element-1134" data-mp="1fyu3vpxvprok">/ month</div>

              <div className="space-y-3 pt-4 border-t border-slate-800/50" data-id="element-1135" data-mp="1us740zu6raay">
                <div className="flex justify-between text-sm" data-id="element-1136" data-mp="1ptm2mx376kkx">
                  <span className="text-slate-400" data-id="element-1137" data-mp="23m261z00gkmd">System Fee</span>
                  <span className="text-white font-medium" data-id="element-1138" data-mp="23laago6eymw3">
                    ${fmt(systemFee)}
                  </span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-1139" data-mp="2djq3wxswy88r">
                  <span className="text-slate-400" data-id="element-1140" data-mp="2di5bpeu2n1e2">
                    {isPartial ? 'Partial Campaign' : campaigns === 1 ? 'Campaign Management' : `Campaign Management (x${campaigns})`}
                  </span>
                  <span className="text-white font-medium" data-id="element-1141" data-mp="2x3p112kpk3e2">
                    ${fmt(campaignFee)}
                  </span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-1142" data-mp="2n72du74wt08v">
                  <span className="text-slate-400" data-id="element-1143" data-mp="29ggwwnz0n0dm">
                    {campaigns === 1 ? 'Corporate Recruiter' : `Corporate Recruiters (x${campaigns})`}
                  </span>
                  <span className="text-white font-medium" data-id="element-1144" data-mp="1zisx3jmjz2ol">
                    ${fmt(corpRecruiter)}
                  </span>
                </div>
              </div>

              <ul className="mt-5 space-y-2.5 pt-4 border-t border-slate-800/50" data-id="element-1145" data-mp="24f1cmy4nwxwa">
                {['Active ad management and real-time optimization', 'Live applicant screening, 14 hrs/day, 7 days/week', 'Second-round interview execution handled by a US-based corporate recruiter representing YOUR brand', 'Monthly recruiting review and ongoing consulting'].map((point, i) => <li key={i} className="flex items-start" data-id="element-1146" data-mp="3ngbm9il8jhsv" data-mpi={i}>
                    <Check className="w-3.5 h-3.5 text-amber-400 mr-2 mt-0.5 shrink-0" data-id="element-1147" data-mp="3ihqkvfu8ys2u" />
                    <span className="text-slate-400 text-sm leading-snug" data-id="element-1148" data-mp="jzv3ijjbcxt6">
                      {point}
                    </span>
                  </li>)}
              </ul>
            </div>
          </div>

          <div className="bg-slate-900/40 border border-slate-800 p-6 rounded-xl" data-id="element-1149" data-mp="a049eiyo0ktm">
            <div className="mb-4" data-id="element-1150" data-mp="60bw5sybtqhx">
              <div className="text-xs text-amber-400 uppercase tracking-widest mb-4" data-id="element-1151" data-mp="5z0vi206gyv1">
                Sales School Tickets
              </div>
              <div className="flex items-baseline mb-2" data-id="element-1152" data-mp="2qq19g1jdt48i">
                <span className="text-3xl font-bold text-white" data-id="element-1153" data-mp="2qohr85e60j43">$150</span>
                <span className="text-slate-400 ml-2 text-sm" data-id="element-1154" data-mp="168s5mplxqmuc">per ticket</span>
              </div>
              <p className="mt-5 pt-4 border-t border-slate-800/50 text-slate-400 text-sm leading-snug" data-id="element-1155" data-mp="1aadm0kxohj9a">
                Sales School tickets are purchased separately based on the number
                of attendees.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-8 p-6 bg-amber-500/5 border border-amber-500/20 rounded-lg max-w-3xl mx-auto" data-id="element-1156" data-mp="1oyrqt1ennhsh">
          <p className="text-amber-400 text-center font-medium" data-id="element-1157" data-mp="1twjq4ghmq66p">
            Ad spend is paid directly to the job platforms and is separate from
            HIRE's fees.
          </p>
        </div>
      </section>

      {/* 2. THE EXECUTION BOTTLENECK */}
      <section className="py-20 px-6 md:px-12 max-w-4xl mx-auto border-t border-slate-800/50" data-id="element-1158" data-mp="22vnbjr49qxod">
        <div className="text-center mb-12" data-id="element-1159" data-mp="3n9qwpjrsn9d">
          <h2 className="text-3xl font-bold text-white mb-4" data-id="element-1160" data-mp="8j6xi458ef8y">
            The Execution Bottleneck
          </h2>
          <p className="text-slate-400 text-lg" data-id="element-1161" data-mp="2ag7nxhyec03h">
            At scale, recruiting doesn't fail because of traffic or systems.
          </p>
        </div>

        <div className="bg-slate-900/30 border border-slate-800 p-8 md:p-12 rounded-xl" data-id="element-1162" data-mp="2k7bzs8srz4u3">
          <h3 className="text-sm text-amber-400 uppercase tracking-widest font-bold mb-6 text-center" data-id="element-1163" data-mp="2k96bqxm2jmm1">
            Execution Constraints
          </h3>
          <ul className="space-y-4 max-w-2xl mx-auto" data-id="element-1164" data-mp="1rjfvoj0vmana">
            {['Leaders are pulled into interviews', 'Follow-up slows down', 'Calendars bottleneck', 'Recruiting becomes a second job'].map((item, idx) => <li key={idx} className="flex items-start text-slate-300" data-id="element-1165" data-mp="1whho3uk6zmbf" data-mpi={idx}>
                <X className="w-5 h-5 text-amber-500/50 mr-3 mt-0.5 flex-shrink-0" data-id="element-1166" data-mp="3cvr33y2jnypl" />
                <span data-id="element-1167" data-mp="3cxu41d58jc93">{item}</span>
              </li>)}
          </ul>
          <p className="text-center text-slate-400 mt-8 pt-8 border-t border-slate-800" data-id="element-1168" data-mp="37wo52xkzesr4">
            Summit exists to remove that bottleneck.
          </p>
        </div>
      </section>

      {/* 3. WHAT CHANGES WITH SUMMIT */}
      <section className="py-20 px-6 md:px-12 bg-slate-900/20 border-y border-slate-800" data-id="element-1169" data-mp="kogt8pkpvqs5">
        <div className="max-w-5xl mx-auto" data-id="element-1170" data-mp="kqtepcpooj9y">
          <h2 className="text-3xl font-bold text-white mb-12 text-center" data-id="element-1171" data-mp="pol707u8q7eo">
            What Changes With Summit
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12" data-id="element-1172" data-mp="1ddxqc0ja7vup">
            <div className="bg-slate-900/40 border border-slate-800 p-6 rounded-xl" data-id="element-1173" data-mp="1hdogq34wc9yp">
              <div className="w-12 h-12 rounded-full bg-slate-800 flex items-center justify-center mb-4 border border-slate-700" data-id="element-1174" data-mp="13o4eqzj53gqg">
                <Shield className="w-6 h-6 text-amber-400" data-id="element-1175" data-mp="18kuvzq6okidg" />
              </div>
              <h3 className="text-white font-bold mb-2" data-id="element-1176" data-mp="1wayvpmgqzxz6">Execution Ownership</h3>
              <p className="text-slate-400 text-sm" data-id="element-1177" data-mp="2138gyp6x49v4">
                Recruiting execution becomes HIRE's responsibility
              </p>
            </div>
            <div className="bg-slate-900/40 border border-slate-800 p-6 rounded-xl" data-id="element-1178" data-mp="vb5rjti44svt">
              <div className="w-12 h-12 rounded-full bg-slate-800 flex items-center justify-center mb-4 border border-slate-700" data-id="element-1179" data-mp="10bk1xi76sex0">
                <Users className="w-6 h-6 text-amber-400" data-id="element-1180" data-mp="qe5kpgdfvm3n" />
              </div>
              <h3 className="text-white font-bold mb-2" data-id="element-1181" data-mp="qg8kut8b2vm3">Managed Throughput</h3>
              <p className="text-slate-400 text-sm" data-id="element-1182" data-mp="cl69hqkowe1w">
                Interview throughput is managed, not improvised
              </p>
            </div>
            <div className="bg-slate-900/40 border border-slate-800 p-6 rounded-xl" data-id="element-1183" data-mp="2ouu2rpsz838">
              <div className="w-12 h-12 rounded-full bg-slate-800 flex items-center justify-center mb-4 border border-slate-700" data-id="element-1184" data-mp="7nnxtsfafkq7">
                <Briefcase className="w-6 h-6 text-amber-400" data-id="element-1185" data-mp="3pt0xle18us2n" />
              </div>
              <h3 className="text-white font-bold mb-2" data-id="element-1186" data-mp="3usvnpfd73dmn">Protected Time</h3>
              <p className="text-slate-400 text-sm" data-id="element-1187" data-mp="3uu6qbbuwmfc6">
                Leadership time is protected for production and growth
              </p>
            </div>
          </div>

          <div className="p-6 bg-amber-500/5 border border-amber-500/20 rounded-lg max-w-3xl mx-auto" data-id="element-1188" data-mp="369btl5psjk79">
            <p className="text-amber-400 font-medium text-center" data-id="element-1189" data-mp="3k8la66hwsgbr">
              Summit does not change the recruiting system — it changes who runs
              it.
            </p>
          </div>
        </div>
      </section>

      {/* 4. OWNERSHIP MAP */}
      <section className="py-20 px-6 md:px-12 max-w-6xl mx-auto" data-id="element-1190" data-mp="3p74uk2gutk20">
        <h2 className="text-3xl font-bold text-white mb-12 text-center" data-id="element-1191" data-mp="7pvfcd52luwk">
          Ownership Map
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16" data-id="element-1192" data-mp="2r0oqp0v321w">
          {/* HIRE Owns */}
          <div className="bg-slate-900/40 border border-amber-500/20 rounded-xl p-8 relative overflow-hidden" data-id="element-1193" data-mp="gqa11dly5c0l">
            <div className="absolute top-0 left-0 w-full h-1 bg-amber-500" data-id="element-1194" data-mp="bs89s5eda6e1" />
            <h3 className="text-xl font-bold text-white mb-8 flex items-center" data-id="element-1195" data-mp="35joqrxkizau9">
              <span className="w-2 h-2 rounded-full bg-amber-500 mr-3" data-id="element-1196" data-mp="35hlpghdx9va8" />
              HIRE Owns
            </h3>
            <ul className="space-y-4" data-id="element-1197" data-mp="3ek4t33pj8bxj">
              {['Dedicated recruiter capacity (operated and managed by HIRE)', 'Interview execution and scheduling', 'Candidate follow-up and throughput', 'Recruiting execution accountability'].map((item, idx) => <li key={idx} className="flex items-start text-slate-300" data-id="element-1198" data-mp="3t7y9urqctkhp" data-mpi={idx}>
                  <Check className="w-5 h-5 text-amber-500 mr-3 mt-0.5 flex-shrink-0" data-id="element-1199" data-mp="226kvvobcdwh" />
                  <span data-id="element-1200" data-mp="641k8umooh7m">{item}</span>
                </li>)}
            </ul>
          </div>

          {/* You Own */}
          <div className="bg-slate-900/40 border border-slate-800 rounded-xl p-8 relative overflow-hidden" data-id="element-1201" data-mp="23v4gfz9h5kl4">
            <div className="absolute top-0 left-0 w-full h-1 bg-slate-600" data-id="element-1202" data-mp="27wg07o5o3ble" />
            <h3 className="text-xl font-bold text-white mb-8 flex items-center" data-id="element-1203" data-mp="20cf3t8hmqnho">
              <span className="w-2 h-2 rounded-full bg-slate-600 mr-3" data-id="element-1204" data-mp="1vloy1u5akimg" />
              You Own
            </h3>
            <ul className="space-y-4" data-id="element-1205" data-mp="1qlu9hx8zo54k">
              {['Final hiring approvals', 'Sales leadership and production', 'Culture and performance expectations'].map((item, idx) => <li key={idx} className="flex items-start text-slate-400" data-id="element-1206" data-mp="2ja87we3zp3gn" data-mpi={idx}>
                  <div className="w-5 h-5 rounded-full bg-slate-800 border border-slate-700 mr-3 mt-0.5 flex-shrink-0 flex items-center justify-center" data-id="element-1207" data-mp="2ed81u37540pj">
                    <div className="w-1.5 h-1.5 rounded-full bg-slate-500" data-id="element-1208" data-mp="25b8ih48nkkjf" />
                  </div>
                  <span data-id="element-1209" data-mp="yv6aaqqvl9tj">{item}</span>
                </li>)}
            </ul>
          </div>
        </div>

        <div className="mt-8 text-center" data-id="element-1210" data-mp="tvjv4brhlji3">
          <p className="text-slate-400 font-medium" data-id="element-1211" data-mp="turzj0xw3lrt">
            Summit transfers execution ownership and staffing risk to HIRE.
          </p>
        </div>
      </section>

      {/* 5. RECRUITER CAPACITY */}
      <section className="py-20 px-6 md:px-12 bg-slate-900/20 border-y border-slate-800" data-id="element-1212" data-mp="2mksr6yt6oo2p">
        <div className="max-w-4xl mx-auto" data-id="element-1213" data-mp="28txmzztmqv5g">
          <h2 className="text-3xl font-bold text-white mb-12 text-center" data-id="element-1214" data-mp="2ctes2s559qa3">
            Recruiter Capacity
          </h2>

          <div className="bg-slate-900/40 border border-slate-800 rounded-xl p-8 md:p-12" data-id="element-1215" data-mp="30kbvfgivjf9q">
            <div className="space-y-6" data-id="element-1216" data-mp="35ilqzs8ighvv">
              <div className="flex items-start" data-id="element-1217" data-mp="35hkedjbujnc1">
                <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center mr-4 flex-shrink-0 border border-slate-700" data-id="element-1218" data-mp="2rj3yb1s3gkh8">
                  <Check className="w-5 h-5 text-amber-400" data-id="element-1219" data-mp="2wfudznzzle24" />
                </div>
                <div data-id="element-1220" data-mp="1g2m8vlmwzi43">
                  <h3 className="text-white font-bold mb-2" data-id="element-1221" data-mp="1g5i9jhq5ckw8">
                    Trained and Managed by HIRE
                  </h3>
                  <p className="text-slate-400 text-sm" data-id="element-1222" data-mp="1cl83x5rmycx1">
                    Recruiters are trained, managed, and quality-controlled by
                    HIRE to maintain consistency and professionalism.
                  </p>
                </div>
              </div>
              <div className="flex items-start" data-id="element-1223" data-mp="1mhl50opift1t">
                <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center mr-4 flex-shrink-0 border border-slate-700" data-id="element-1224" data-mp="1hkb9qsffwn62">
                  <Check className="w-5 h-5 text-amber-400" data-id="element-1225" data-mp="tsxg8j5jylji" />
                </div>
                <div data-id="element-1226" data-mp="oxi5i02ud5rb">
                  <h3 className="text-white font-bold mb-2" data-id="element-1227" data-mp="ou2mim61rckm">
                    Represents Your Brand
                  </h3>
                  <p className="text-slate-400 text-sm" data-id="element-1228" data-mp="yucyk3gcidy7">
                    Recruiters represent the client's brand with professionalism
                    and alignment to company values.
                  </p>
                </div>
              </div>
              <div className="flex items-start" data-id="element-1229" data-mp="655smjekam2x">
                <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center mr-4 flex-shrink-0 border border-slate-700" data-id="element-1230" data-mp="2686yzk2x4qd7">
                  <Check className="w-5 h-5 text-amber-400" data-id="element-1231" data-mp="2b4xeo6at9jy3" />
                </div>
                <div data-id="element-1232" data-mp="2fyrswu7nd6ps">
                  <h3 className="text-white font-bold mb-2" data-id="element-1233" data-mp="2fy9ym34jp2ki">
                    Designed for Consistency
                  </h3>
                  <p className="text-slate-400 text-sm" data-id="element-1234" data-mp="1n8t48zxd7bln">
                    Capacity is designed to support consistent interview flow,
                    not heroic bursts.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 7. WHO SUMMIT IS FOR / NOT FOR */}
      <section className="py-20 px-6 md:px-12 bg-slate-900/20 border-y border-slate-800" data-id="element-1235" data-mp="219dlhrnmszd1">
        <div className="max-w-5xl mx-auto" data-id="element-1236" data-mp="37nmnss2mdtvz">
          <h2 className="text-3xl font-bold text-white mb-12 text-center" data-id="element-1237" data-mp="37r0ozwzjrx1u">
            Who Summit Is For
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12" data-id="element-1238" data-mp="3mrelyyw46ojh">
            <div data-id="element-1239" data-mp="3mtsd4wnobglr">
              <h3 className="text-lg font-bold text-white mb-6 flex items-center" data-id="element-1240" data-mp="3hsl4ywhmcfek">
                <Check className="w-5 h-5 text-amber-500 mr-2" data-id="element-1241" data-mp="3huo3y60r1ivf" />
                Summit is a fit if you:
              </h3>
              <ul className="space-y-4" data-id="element-1242" data-mp="390j7n72ujf8i">
                {['Want recruiting execution off your plate', 'Believe sales leaders should be selling, not interviewing', 'Prefer external ownership over internal hiring', 'Trust HIRE to manage recruiter performance'].map((item, idx) => <li key={idx} className="text-slate-400 flex items-start" data-id="element-1243" data-mp="2u53b8vesn78y" data-mpi={idx}>
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mr-3 mt-2" data-id="element-1244" data-mp="2q2zwnxwtvmjg" />
                    {item}
                  </li>)}
              </ul>
            </div>
            <div data-id="element-1245" data-mp="obdcaf7fypvy">
              <h3 className="text-lg font-bold text-white mb-6 flex items-center" data-id="element-1246" data-mp="oatrmqqepne8">
                <X className="w-5 h-5 text-slate-500 mr-2" data-id="element-1247" data-mp="17jbwu8fivxpm" />
                Summit is not a fit if you:
              </h3>
              <ul className="space-y-4" data-id="element-1248" data-mp="1bmhwuyc1ksnn">
                {['Want to manage recruiters internally', 'Prefer hands-on control over execution', 'Only want infrastructure or consulting'].map((item, idx) => <li key={idx} className="text-slate-400 flex items-start" data-id="element-1249" data-mp="nt9mpmxx1l2t" data-mpi={idx}>
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-600 mr-3 mt-2" data-id="element-1250" data-mp="suyv0utr7kn7" />
                    {item}
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 8. HOW THIS SCALES */}
      <section className="py-16 px-6 md:px-12 bg-slate-900/20 border-y border-slate-800 text-center" data-id="element-1251" data-mp="298fd0p2y8lwf">
        <div className="max-w-3xl mx-auto" data-id="element-1252" data-mp="2e6z22ycuadt0">
          <h2 className="text-2xl font-bold text-white mb-8" data-id="element-1253" data-mp="2e6qmuzt8uwen">
            How This Scales
          </h2>
          <div className="flex flex-col md:flex-row justify-center gap-6 text-left" data-id="element-1254" data-mp="2obfdnfjad8r0">
            <div className="flex-1 p-6 border border-slate-800 rounded-lg bg-slate-900/40" data-id="element-1255" data-mp="2jddsmp2auzcw">
              <div className="text-xs text-slate-500 uppercase tracking-widest mb-2" data-id="element-1256" data-mp="2x4amej7ypmlj">
                Foundation
              </div>
              <h3 className="text-white font-bold mb-2" data-id="element-1257" data-mp="2sao8knq2bco5">Advantage</h3>
              <p className="text-slate-400 text-sm" data-id="element-1258" data-mp="370dk9cvs6z2o">
                Where most teams start before scaling execution.
              </p>
            </div>
            <div className="flex-1 p-6 border border-amber-500/30 rounded-lg bg-slate-800/30" data-id="element-1259" data-mp="3g23d8n5ba62k">
              <div className="text-xs text-amber-400 uppercase tracking-widest mb-2" data-id="element-1260" data-mp="3c0run1kuul3v">
                Execution
              </div>
              <h3 className="text-white font-bold mb-2" data-id="element-1261" data-mp="1lpdiy4b16uun">Summit</h3>
              <p className="text-slate-400 text-sm" data-id="element-1262" data-mp="1hmqpjyypp1vc">
                Recruiting execution handled for you.
              </p>
            </div>
            <div className="flex-1 p-6 border border-slate-800 rounded-lg bg-slate-900/40" data-id="element-1263" data-mp="ozh78x4ef0v1">
              <div className="text-xs text-slate-500 uppercase tracking-widest mb-2" data-id="element-1264" data-mp="k0ntr4p439uv">
                Leadership
              </div>
              <h3 className="text-white font-bold mb-2" data-id="element-1265" data-mp="ypseasjdpq65">Leadership</h3>
              <p className="text-slate-400 text-sm" data-id="element-1266" data-mp="tsthu3c8t74s">
                Add it when finding leadership becomes the bottleneck.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 9. CTA */}
      <section className="py-32 px-6 md:px-12 text-center border-t border-slate-800/50" data-id="element-1267" data-mp="14nvxd9gdc3d">
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-8" data-id="element-1268" data-mp="f45pe5xp1dj2">
          See if Summit is the right execution model for your team.
        </h2>
        <button className="inline-flex items-center px-8 py-4 bg-amber-600 hover:bg-amber-700 text-white font-bold rounded-lg transition-all" data-id="element-1269" data-mp="1qh86r37ma74a">
          Discuss Summit
          <ArrowRight className="ml-2 w-5 h-5" data-id="element-1270" data-mp="1miikel4lpf8i" />
        </button>
      </section>
    </div>;
}